import * as H from 'history';
import { CardModel } from 'src/types/card.model';
import CardService from './card.service';
import MixpanelService from './mixpanel.service';
import StorageService from './storage.service';
import TrackService from './tracking.service';
import UrlHelper from 'src/utils/url.helper';

/**
 * This is how we change views in the app. If we are changing views another way, chances are that is deprecated
 * and should be changed.
 */
export default class NavigationService {
    constructor() {
        StorageService.setInternalRoute(null);
    }

    public static async goBack(history: H.History): Promise<void> {
        StorageService.getInternalRoute() ? history.goBack() : await this.goTo(history, '/');
    }

    private static pathBeforeAddAccounts: string = '';

    public static async goTo(history: H.History, nextPath: string, label: string = ''): Promise<void> {
        StorageService.setInternalRoute(true);
        if (!UrlHelper.includesBasePath(nextPath)) nextPath = UrlHelper.getFullPath(nextPath);
        const currentPath: string = window.location.pathname;
        TrackService.track({ url: nextPath }).catch(console.error);

        const trackingPath: string = UrlHelper.stripBasePath(nextPath);
        const currentSubPath: string = UrlHelper.stripBasePath(currentPath);

        if (nextPath.includes('add-accounts')) {
            NavigationService.pathBeforeAddAccounts = currentPath;
        }

        switch (true) {
            case currentPath.includes('budget') && !nextPath.includes('budget'): {
                await history.push({
                    pathname: nextPath
                });
                window.dispatchEvent(new Event('resize'));
                return;
            }
            case currentPath.includes('budget'): {
                await history.replace({
                    pathname: nextPath
                });
                return;
            }
            case (currentPath.includes('crypto') || currentPath.includes('investment')) &&
                NavigationService.pathBeforeAddAccounts.includes('card'): {
                // if going from a card to add-accounts to crypto or investment then go back to the card after adding account
                await history.push({ pathname: NavigationService.pathBeforeAddAccounts });
                NavigationService.pathBeforeAddAccounts = '';
                window.dispatchEvent(new Event('resize'));
                return;
            }
            case currentPath.includes('crypto'): {
                await history.push({
                    pathname: nextPath
                });
                window.dispatchEvent(new Event('resize'));
                return;
            }
            case currentPath.includes('transactions') && !nextPath.includes('transactions'): {
                // if going to transactions from someplace else, push on stack
                await history.push({
                    pathname: nextPath
                });
                window.dispatchEvent(new Event('resize'));
                return;
            }
            case currentPath.includes('transactions'):
                // if going to transactions and we were already on transactions, replace it
                history.replace({
                    pathname: nextPath
                });
                return;
            case currentPath.includes('card'):
                const phrases = trackingPath.split('/');
                const id = Number(phrases[phrases.length - 1]);
                let card: CardModel = null;
                if (id) {
                    card = CardService.getCard(id);
                }

                MixpanelService.track('URL', { url: trackingPath, card: card ? card.description : 'unknown', label });

                // handle clicking from card to connect accounts, special case where we push
                if (currentSubPath.includes('card') && nextPath.includes('add-accounts')) {
                    history.push({
                        pathname: nextPath
                    });
                    return;
                }

                await history.replace({
                    pathname: nextPath
                });
                return;
            case currentPath.includes('show') && nextPath.includes('show'):
                MixpanelService.track('URL', { url: trackingPath, label });
                await history.replace({
                    pathname: nextPath
                });
                return;
            case nextPath === '/drawer/profile' && NavigationService.pathBeforeAddAccounts.includes('card'): {
                // if going from card to add accounts to add sync account, go back to card instead of base path after adding account
                await history.push({ pathname: NavigationService.pathBeforeAddAccounts });
                NavigationService.pathBeforeAddAccounts = '';
                window.dispatchEvent(new Event('resize'));
                return;
            }
            default:
                MixpanelService.track('URL', { url: trackingPath, label });
                await history.push({
                    pathname: nextPath
                });
        }
    }

    /** Retrieves the global amex header */
    public static async getGlobalHeader(): Promise<string> {
        if (!process.env.REACT_APP_GLOBAL_HEADER) return null;

        try {
            const response: Response = await fetch(process.env.REACT_APP_GLOBAL_HEADER, {
                headers: {
                    Accept: 'text/html'
                },
                mode: 'cors'
            });
            if (!response.ok) {
                throw new Error('Global Amex Header network response was not ok');
            }
            return await response.text();
        } catch (error: any) {
            console.error('Error fetching global header:', error);
            return '';
        }
    }

    /** Retrieves the global amex footer */
    public static async getGlobalFooter(): Promise<string> {
        if (!process.env.REACT_APP_GLOBAL_FOOTER) return null;
        try {
            const response: Response = await fetch(process.env.REACT_APP_GLOBAL_FOOTER, {
                headers: {
                    Accept: 'text/html'
                },
                mode: 'cors'
            });
            if (!response.ok) {
                throw new Error('Global Amex Footer network response was not ok');
            }
            return await response.text();
        } catch (error: any) {
            console.error('Error fetching global footer:', error);
            return '';
        }
    }
}
